import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getActiveLedger,
  getAllBankingListing,
  getBankingIndents,
  getBankingPlaced,
  getBankingReceived,
  getDocumentHistory,
  getDocumentUrlByLedgerId,
  getInterestedCompanies,
  getLedgerBanking,
  getLedgerOngoing,
  getMatchmakingResults,
  getSearchBanking,
  getConnectionHistory,
  getInjectDrawalQuantity,
  getViewDocumentHistory,
  closeBankingListing,
  getTermsDoc,
  downloadView,
  getBankingArchive,
  getArchiveList,
  getWithdrawnArchive,
} from "../../Services/ApiCalling/banking";

export const fetchAllBankingListings = createAsyncThunk(
  "banking/fetchAllBankingListings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllBankingListing();
      return response?.data?.data?.page || [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch listings"
      );
    }
  }
);

export const fetchBankingReceived = createAsyncThunk(
  "banking/fetchBankingReceived",
  async ({ id, currentPage, size, isInitiator }, { rejectWithValue }) => {
    try {
      const response = await getBankingReceived(
        id,
        currentPage,
        size,
        isInitiator
      );
      return response?.data?.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch banking data"
      );
    }
  }
);

export const fetchBankingPlaced = createAsyncThunk(
  "banking/fetchBankingPlaced",
  async ({ id, currentPage, size, isInitiator }, { rejectWithValue }) => {
    try {
      const response = await getBankingPlaced(
        id,
        currentPage,
        size,
        isInitiator
      );
      const data = response?.data?.data?.page || [];

      const connectedIds = data.map((item) => item.bankingListingId);

      return { data, connectedIds };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch banking data"
      );
    }
  }
);

export const fetchMatchmakingResults = createAsyncThunk(
  "banking/fetchMatchmakingResults",
  async ({ id, statusFilter = null }, { rejectWithValue }) => {
    try {
      const response = await getMatchmakingResults(
        id,
        0,
        1000,
        null,
        statusFilter
      );
      return response?.data?.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch matchmaking data"
      );
    }
  }
);

//not sure
export const fetchBankingIndents = createAsyncThunk(
  "banking/fetchBankingIndents",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await getBankingIndents(id);

      return response?.data?.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch banking data"
      );
    }
  }
);
export const fetchActiveLedger = createAsyncThunk(
  "banking/fetchActiveLedger",
  async (
    { currentPage = 0, size = 20, bankingListingId = null },
    { rejectWithValue }
  ) => {
    try {
      const response = await getActiveLedger(
        currentPage,
        size,
        bankingListingId
      );
      return response?.data?.data?.page || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch ledger banking data"
      );
    }
  }
);

export const fetchLedgerBanking = createAsyncThunk(
  "banking/fetchLedgerBanking",
  async ({ currentPage = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await getLedgerBanking(currentPage, size);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch search banking data"
      );
    }
  }
);

export const fetchLedgerOngoing = createAsyncThunk(
  "banking/fetchLedgerOngoing",
  async ({ currentPage = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await getLedgerOngoing(currentPage, size);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch search banking data"
      );
    }
  }
);

export const fetchConnectionHistory = createAsyncThunk(
  "connectionHistory/fetchConnectionHistory",
  async (listingId, { rejectWithValue }) => {
    try {
      const response = await getConnectionHistory(listingId);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInjectQuantity = createAsyncThunk(
  "connectionHistory/fetchInjectQuantity",
  async (listingId, { rejectWithValue }) => {
    try {
      const response = await getInjectDrawalQuantity(listingId);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSearchBanking = createAsyncThunk(
  "banking/fetchSearchBanking",
  async ({ current = true }, { rejectWithValue }) => {
    try {
      const response = await getSearchBanking(current);
      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch listings"
      );
    }
  }
);

export const fetchInterestedCompanies = createAsyncThunk(
  "banking/fetchInterestedCompanies",
  async ({ listingId, currentPage = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await getInterestedCompanies(
        listingId,
        currentPage,
        size
      );
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDocumentUrlByLedgerId = createAsyncThunk(
  "document/fetchByLedgerId",
  async (ledgerDocumentId, { rejectWithValue }) => {
    try {
      const response = await getDocumentUrlByLedgerId(ledgerDocumentId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDownloadDocument = createAsyncThunk(
  "document/fetchDownloadDocument",
  async (ledgerDocumentId, { rejectWithValue }) => {
    try {
      const response = await downloadView(ledgerDocumentId);
      const blob = response?.data;

      // Validate blob
      if (!blob || blob.size === 0) {
        return rejectWithValue("Empty document received");
      }

      return blob;
    } catch (error) {
      console.error("Download error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDocumentHistory = createAsyncThunk(
  "banking/fetchDocumentHistory",
  async ({ connectionId, documentType }, { rejectWithValue }) => {
    try {
      const response = await getDocumentHistory(connectionId, documentType);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch document");
    }
  }
);

export const fetchViewDocumentHistory = createAsyncThunk(
  "banking/fetchViewDocumentHistory",
  async (connectedId, { rejectWithValue }) => {
    try {
      const response = await getViewDocumentHistory(connectedId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const terminateBankingListing = createAsyncThunk(
  "banking/closeListing",
  async (id, { rejectWithValue }) => {
    try {
      const response = await closeBankingListing(id);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchTermsDoc = createAsyncThunk(
  "documents/fetchTermsDoc",
  async (docId, { rejectWithValue }) => {
    try {
      const result = await getTermsDoc(docId);
      let docData = null;

      if (result?.data?.data) {
        docData = result.data.data;
      } else if (result?.data) {
        docData = result.data;
      } else if (result) {
        docData = result;
      }
      if (docData && docData.filename) {
        return { fileName: docData.filename, docId: docData.id };
      }

      return rejectWithValue("Filename not found in response");
    } catch (error) {
      console.error("Error in fetchTermsDoc thunk:", error);
      return rejectWithValue(error.message || "Failed to fetch document");
    }
  }
);

export const fetchBankingArchive = createAsyncThunk(
  "bankingArchive/fetch",
  async (
    { status = "PAST", currentPage = 0, size = 10 },
    { rejectWithValue }
  ) => {
    try {
      const response = await getBankingArchive(status, currentPage, size);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchArchiveList = createAsyncThunk(
  "archive/fetchArchiveList",
  async ({ listingId, status }, { rejectWithValue }) => {
    try {
      const res = await getArchiveList(0, 10, listingId, status);
      return res?.data?.data?.page;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch archive list"
      );
    }
  }
);

export const fetchArchiveWithdrawanList = createAsyncThunk(
  "archive/fetchWithdrawnArchiveList",
  async ({ listingId, status }, { rejectWithValue }) => {
    try {
      const res = await getWithdrawnArchive(0, 10, listingId, status);
      return res?.data?.data?.page;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch withdrawn archive list"
      );
    }
  }
);

const bankingSlice = createSlice({
  name: "banking",
  initialState: {
    allListings: [],
    matchmakingData: [],
    activeListingId: null,
    counterPartyListingId: null,
    loading: false,
    error: null,
    generatedLoa: null,
    bankingPlacedData: [],
    ledgerBankingData: {
      page: [],
      currentPage: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    },
    ledgerOngoingData: {
      page: [],
      currentPage: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    },

    connectedListingIds: [],
    activeLedgerData: [],
    connectionHistory: [],
    lastDirectUpdate: null,
  },
  reducers: {
    setActiveListingId: (state, action) => {
      state.activeListingId = action.payload;
    },
    setCounterPartyListingId: (state, action) => {
      state.counterPartyListingId = action.payload;
    },
    clearMatchmakingData: (state) => {
      state.matchmakingData = [];
    },
    addNewCounterPartyListingBanking: (state, action) => {
      JSON.parse(JSON.stringify(state.matchmakingData));
      if (Array.isArray(state.matchmakingData)) {
        state.matchmakingData.unshift(action.payload);
      } else if (state.matchmakingData && state.matchmakingData.matches) {
        state.matchmakingData.matches.unshift(action.payload);
      } else {
        state.matchmakingData = [action.payload];
      }
    },
    updateMatchesListingData: (state, action) => {
      const { bidId, status } = action.payload;
      const listing = state.matchmakingData?.matches.find(
        (l) => l.bidId === bidId
      );
      if (listing) {
        listing.status = status;
      }
    },
    updateBankingBidStatus: (state, action) => {
      const { bidId, status, id } = action.payload;

      if (
        state.matchmakingData &&
        Array.isArray(state.matchmakingData.matches)
      ) {
        const matchIndex = state.matchmakingData.matches.findIndex(
          (match) =>
            String(match.bidId) === String(bidId) ||
            String(match.id) === String(id)
        );

        if (matchIndex !== -1) {
          state.matchmakingData.matches[matchIndex].status = status;
        }
      }
    },
    updateBankingMatchmakingData: (state, action) => {
      state.matchmakingData = action.payload;
      state.lastDirectUpdate = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankingReceived.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankingReceived.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || {};
      })
      .addCase(fetchBankingReceived.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch banking details";
      });
    builder
      .addCase(fetchBankingPlaced.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankingPlaced.fulfilled, (state, action) => {
        state.loading = false;
        state.bankingPlacedData = action.payload.data;
        state.connectedListingIds = action.payload.connectedIds || [];
        state.data = action.payload || {};
      })
      .addCase(fetchBankingPlaced.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch banking details";
      });
    builder
      .addCase(terminateBankingListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(terminateBankingListing.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(terminateBankingListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to close listing";
      });
    builder
      .addCase(fetchAllBankingListings.pending, (state) => {
        state.loadingListings = true;
        state.error = null;
      })
      .addCase(fetchAllBankingListings.fulfilled, (state, action) => {
        state.loadingListings = false;
        state.allListings = action.payload;
      })
      .addCase(fetchAllBankingListings.rejected, (state, action) => {
        state.loadingListings = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchMatchmakingResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatchmakingResults.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData && typeof responseData === "object") {
          state.matchmakingData = responseData;
        } else {
          state.matchmakingData = { matches: [], selfListing: null };
        }
      })
      .addCase(fetchMatchmakingResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchConnectionHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConnectionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionData = action.payload || [];
      })
      .addCase(fetchConnectionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch connection history";
      });
    builder
      .addCase(fetchInjectQuantity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInjectQuantity.fulfilled, (state, action) => {
        state.loading = false;
        state.injectDrawalQty = action.payload || [];
      })
      .addCase(fetchInjectQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch connection history";
      });
    builder
      .addCase(fetchBankingIndents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankingIndents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || {};
      })
      .addCase(fetchBankingIndents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch banking details";
      });
    builder
      .addCase(fetchActiveLedger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveLedger.fulfilled, (state, action) => {
        state.loading = false;
        state.activeLedgerData = action.payload;
      })
      .addCase(fetchActiveLedger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchLedgerBanking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLedgerBanking.fulfilled, (state, action) => {
        state.loading = false;
        state.ledgerBankingData = action.payload;
      })
      .addCase(fetchLedgerBanking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchLedgerOngoing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLedgerOngoing.fulfilled, (state, action) => {
        state.loading = false;
        state.ledgerOngoingData = action.payload;
      })
      .addCase(fetchLedgerOngoing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchSearchBanking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchBanking.fulfilled, (state, action) => {
        state.loading = false;
        state.allListings = action.payload;
      })
      .addCase(fetchSearchBanking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchInterestedCompanies.pending, (state) => {
        state.interestedCompaniesLoading = true;
        state.interestedCompaniesError = null;
      })
      .addCase(fetchInterestedCompanies.fulfilled, (state, action) => {
        state.interestedCompaniesLoading = false;
        state.interestedCompanies = action.payload;
      })
      .addCase(fetchInterestedCompanies.rejected, (state, action) => {
        state.interestedCompaniesLoading = false;
        state.interestedCompaniesError = action.payload;
      });
    builder
      .addCase(fetchDocumentUrlByLedgerId.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.url = null;
      })
      .addCase(fetchDocumentUrlByLedgerId.fulfilled, (state, action) => {
        state.loading = false;
        state.url = action.payload?.url || action.payload;
      })
      .addCase(fetchDocumentUrlByLedgerId.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          action.error.message ||
          "Failed to fetch document URL";
        state.url = null;
      });
    builder
      .addCase(fetchDownloadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.url = null;
      })
      .addCase(fetchDownloadDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.url = action.payload?.url || action.payload;
      })
      .addCase(fetchDownloadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          action.error.message ||
          "Failed to fetch document URL";
        state.url = null;
      });
    builder
      .addCase(fetchDocumentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.documentHistory = action.payload || [];
      })
      .addCase(fetchDocumentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchViewDocumentHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchViewDocumentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.documentViewHistory = action.payload;
      })
      .addCase(fetchViewDocumentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchTermsDoc.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fileName = null;
        state.docId = null;
      })
      .addCase(fetchTermsDoc.fulfilled, (state, action) => {
        state.loading = false;
        state.fileName = action.payload.fileName;
        state.docId = action.payload.docId;
      })
      .addCase(fetchTermsDoc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchBankingArchive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankingArchive.fulfilled, (state, action) => {
        state.loading = false;

        const res = action.payload?.data;

        state.archiveList = res?.page || [];
        state.totalElements = res?.totalElements || 0;
        state.totalPages = res?.totalPages || 0;
        state.currentPage = res?.currentPage || 0;
        state.size = res?.size || 10;
      })
      .addCase(fetchBankingArchive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch archive";
      });
    builder
      // FETCH ARCHIVE LIST
      .addCase(fetchArchiveList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchiveList.fulfilled, (state, action) => {
        state.loading = false;
        state.archiveListConnection = action.payload || [];
      })
      .addCase(fetchArchiveList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      // FETCH ARCHIVE LIST
      .addCase(fetchArchiveWithdrawanList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchiveWithdrawanList.fulfilled, (state, action) => {
        state.loading = false;
        state.archiveWithdrawnListConnection = action.payload || [];
      })
      .addCase(fetchArchiveWithdrawanList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setActiveListingId,
  setCounterPartyListingId,
  clearMatchmakingData,
  addNewCounterPartyListingBanking,
  updateMatchesListingData,
  updateBankingBidStatus,
  updateBankingMatchmakingData,
} = bankingSlice.actions;

export default bankingSlice.reducer;
